import React from "react";
import {
  Chip,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import {
  CountryBlockStatus,
  PendingPatch,
  describeBlockedSince,
  isIpOnlyBlock,
  isPending,
  phoneCoverageLabel,
} from "./countryBlock";

interface PropsInterface {
  countries: Array<CountryBlockStatus>;
  /** The row currently mid-write, by alpha-2 code. */
  savingCode: string | null;
  patches: Map<string, PendingPatch>;
  onToggle: (country: CountryBlockStatus, blocked: boolean) => void;
}

export const CountryBlockTable: React.FC<PropsInterface> = ({
  countries,
  savingCode,
  patches,
  onToggle,
}) => {
  if (!countries.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
        No countries match this search.
      </Typography>
    );
  }

  return (
    // Capped rather than paginated: the whole list is already in memory, and
    // paginating it would hide the "Blocked only" answer behind page controls.
    <TableContainer sx={{ overflowX: "auto", maxHeight: 560 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Country</TableCell>
            <TableCell>Phone code</TableCell>
            <TableCell>Continent</TableCell>
            <TableCell>Capital</TableCell>
            <TableCell>Blocked since</TableCell>
            <TableCell align="right">Blocked</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* Server order, untouched — the API sorts by name. */}
          {countries.map((country) => {
            const saving = savingCode === country.countryCode;
            const pending = isPending(country, patches);

            return (
              <TableRow
                key={country.countryCode}
                sx={{
                  backgroundColor: country.blocked
                    ? (theme) => alpha(theme.palette.error.main, 0.08)
                    : "transparent",
                }}
              >
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    rowGap={0.5}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={country.blocked ? 700 : 400}
                    >
                      {country.name}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {country.countryCode}
                      {country.iso3 ? ` · ${country.iso3}` : ""}
                    </Typography>

                    {!country.recognized && (
                      <Tooltip title="This code is stored in the blocklist but ISO does not know it. It is still enforced — unblocking is the only way to remove it.">
                        <Chip size="small" color="warning" label="Unrecognised" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>

                <TableCell>
                  {country.phoneCode ? (
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {country.phoneCode}
                    </Typography>
                  ) : (
                    /*
                     * No code means the country shares one, which means its
                     * numbers cannot be matched to it. Left as bare "—" this
                     * would read as missing data next to a row saying
                     * "Blocked"; the chip is what stops an admin concluding
                     * the numbers are being turned away.
                     */
                    <Tooltip
                      title={
                        country.phoneBlockingNote ??
                        "This country's dialling code is shared with others, so phone numbers cannot be matched to it. Only the IP check applies."
                      }
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        color={isIpOnlyBlock(country) ? "warning" : "default"}
                        label={phoneCoverageLabel(country)}
                      />
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {country.continent || "—"}
                  </Typography>
                  {country.region && (
                    <Typography variant="caption" color="text.secondary">
                      {country.region}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {country.capital || "—"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {describeBlockedSince(country)}
                  </Typography>

                  {pending && (
                    <Typography variant="caption" color="text.secondary">
                      Saved — applies within a minute
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="right">
                  <Switch
                    checked={country.blocked}
                    disabled={saving}
                    color="error"
                    inputProps={{
                      "aria-label": `Block ${country.name}`,
                    }}
                    onChange={(event) =>
                      onToggle(country, event.target.checked)
                    }
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
